/**
 * 定价页（套餐购买）。个人/团队两 tab + 年/季/月周期切换（只金额变、条款不变）+
 * 竞品式"不同档不同色"卡片 + 金额与加送%（无积分数）+ 购买弹窗。
 * 数据优先 GET /api/v2/packages（后台改了即时生效），失败回退内置 PACKAGES。
 */
import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

import {
  PACKAGES,
  PERIODS,
  bonusPct,
  formatYuan,
  mapPackageRow,
  priceCents,
  type Package,
  type PackageKind,
  type Period,
} from '../../model/packages';
import { PurchaseModal } from '../../features/pricing/PurchaseModal';

const LEVEL_TAG: Record<Package['level'], string> = { standard: '入门', advanced: '进阶', premium: '推荐', deluxe: '尊享' };

export function PricingPage() {
  const [kind, setKind] = useState<PackageKind>('personal');
  const [period, setPeriod] = useState<Period>('year');
  const [pkgs, setPkgs] = useState<Package[]>(PACKAGES);
  const [buy, setBuy] = useState<Package | null>(null);

  useEffect(() => {
    void fetch('/api/v2/packages')
      .then((r) => r.json())
      .then((env: { data?: { items?: unknown[] } }) => {
        const items = env?.data?.items;
        if (Array.isArray(items) && items.length > 0) {
          setPkgs(items.map((r) => mapPackageRow(r as Record<string, unknown>)));
        }
      })
      .catch(() => {
        /* 离线/未配库：用内置兜底 */
      });
  }, []);

  const list = pkgs.filter((p) => p.kind === kind && p.active).sort((a, b) => a.sort - b.sort);
  const segBtn = (on: boolean): string => `rounded-control px-4 py-1.5 text-ui transition-colors ${on ? 'bg-accent font-semibold text-accent-ink' : 'text-ink-secondary hover:text-ink-primary'}`;

  return (
    <div className="relative h-full overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-[1400px] flex-col px-6 py-10">
        <header className="mb-6 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">选择适合你的套餐</h1>
          <p className="mt-2 text-ui text-ink-muted">额度以百分比呈现、用多少扣多少；升级/加送一目了然，无隐藏积分。</p>
        </header>

        {/* 个人 / 团队 */}
        <div className="mb-4 flex justify-center gap-1.5">
          <button type="button" onClick={() => setKind('personal')} className={segBtn(kind === 'personal')}>个人版 · 创作会员</button>
          <button type="button" onClick={() => setKind('team')} className={segBtn(kind === 'team')}>团队版会员</button>
        </div>

        {/* 年 / 季 / 月 */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex gap-1 rounded-card border border-surface-line bg-surface-raised p-1">
            {PERIODS.map((p) => (
              <button key={p.k} type="button" onClick={() => setPeriod(p.k)} className={`relative rounded-control px-4 py-1.5 text-ui transition-colors ${period === p.k ? 'bg-accent/15 font-semibold text-accent' : 'text-ink-secondary hover:text-ink-primary'}`}>
                {p.label}
                <span className="ml-1.5 text-[10px] text-ink-faint">{p.tag}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 档位卡 */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((p) => (
            <div key={p.code} className={`panel tier-card tier-${p.level} flex flex-col gap-3 p-5`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{p.name}</h3>
                <span className={`tier-badge rounded-full px-2 py-0.5 text-[11px] font-semibold`}>{LEVEL_TAG[p.level]}</span>
              </div>
              <div className="flex items-end gap-1">
                <span className="tier-price text-3xl font-bold">{formatYuan(priceCents(p, period))}</span>
                <span className="mb-1 text-uiSm text-ink-muted">/{period === 'year' ? '年' : period === 'quarter' ? '季' : '月'}{p.perSeat ? '/席位' : ''}</span>
              </div>
              <div className="text-uiSm text-ink-muted">
                {bonusPct(p, period) > 0 && <span className="tier-badge mr-2 rounded px-1.5 py-0.5 text-[11px] font-semibold">加送 {bonusPct(p, period)}%</span>}
                {p.perSeat && <span>{p.seatsMin}–{p.seatsMax} 席位</span>}
                {p.storageGb > 0 && <span className="ml-2">素材云空间 {p.storageGb}GB</span>}
                {p.kind === 'personal' && <span>本地存储 · 无需上传</span>}
              </div>
              <ul className="flex flex-1 flex-col gap-1.5 border-t border-surface-line pt-3 text-uiSm text-ink-secondary">
                {p.features.map((f) => (
                  <li key={f} className="flex items-center gap-1.5">
                    <Check size={13} className="shrink-0 text-accent" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setBuy(p)} className="tier-buy mt-1 w-full rounded-control py-2.5 text-ui font-semibold transition">
                开通{p.name}
              </button>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-uiSm text-ink-faint">支付与开通在接入后端后生效；当前为套餐展示框架，价格/权益可在超管后台实时增删改、无需改代码。</p>
      </div>

      {buy !== null && <PurchaseModal pkg={buy} initialPeriod={period} onClose={() => setBuy(null)} />}
    </div>
  );
}
